// Thin, feature-detected wrapper over navigator.mediaSession.
//
// Drives lock-screen / Control-Center media controls (play/pause/seek) on
// mobile browsers that support the Media Session API. Every access is
// guarded — this module is a silent no-op on any browser/environment
// (including happy-dom in tests) where the API is absent.

export interface MediaSessionHandlers {
  onPlay: () => void;
  onPause: () => void;
  onSeek: (sec: number) => void; // absolute seek target in seconds
}

export interface MediaSessionMetadata {
  title: string;
  artist?: string;
  album?: string;
  artwork?: Array<{ src: string; sizes?: string; type?: string }>;
}

// Last position reported via setPositionState, used to compute absolute
// seek targets for the relative seekforward/seekbackward actions.
let lastPosition = 0;

export function isMediaSessionSupported(): boolean {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator;
}

export function wireMediaSession(handlers: MediaSessionHandlers, metadata: MediaSessionMetadata): void {
  if (!isMediaSessionSupported()) return;

  const mediaSession = navigator.mediaSession;

  if (typeof MediaMetadata !== 'undefined') {
    try {
      mediaSession.metadata = new MediaMetadata({
        title: metadata.title,
        artist: metadata.artist,
        album: metadata.album,
        artwork: metadata.artwork,
      });
    } catch {
      // Some browsers throw on malformed metadata (e.g. bad artwork URLs).
    }
  }

  const setHandler = (action: MediaSessionAction, handler: (details: MediaSessionActionDetails) => void): void => {
    try {
      mediaSession.setActionHandler(action, handler);
    } catch {
      // Unsupported action on this browser — ignore.
    }
  };

  setHandler('play', () => handlers.onPlay());
  setHandler('pause', () => handlers.onPause());
  setHandler('seekto', (details) => {
    if (typeof details.seekTime === 'number') handlers.onSeek(details.seekTime);
  });
  setHandler('seekforward', (details) => {
    handlers.onSeek(lastPosition + (details.seekOffset ?? 10));
  });
  setHandler('seekbackward', (details) => {
    handlers.onSeek(lastPosition - (details.seekOffset ?? 10));
  });
}

// Reflect play/pause on the lock screen so the OS shows the correct transport
// button (▶ vs ⏸) and treats the session as active. Feature-detected no-op.
export function setPlaybackState(state: 'playing' | 'paused' | 'none'): void {
  if (!isMediaSessionSupported()) return;
  try {
    navigator.mediaSession.playbackState = state;
  } catch {
    // Some engines don't expose a settable playbackState — ignore.
  }
}

export function setPositionState(state: { duration: number; position: number; playbackRate?: number }): void {
  lastPosition = state.position;

  if (!isMediaSessionSupported()) return;

  const mediaSession = navigator.mediaSession;
  if (typeof mediaSession.setPositionState !== 'function') return;

  try {
    mediaSession.setPositionState({
      duration: state.duration,
      position: state.position,
      playbackRate: state.playbackRate ?? 1,
    });
  } catch {
    // Some browsers throw for out-of-range position/duration combos.
  }
}
